import { useState } from 'react';
import { useMutation } from '@apollo/client';

import useToast from '../../../../customHooks/useToast';
import { IRentPaymentModal } from './RentPaymentModal.types';
import { GENERATE_EQUIFAX_METRO_2_FILE, MARK_AS_SUBMITTED_TO_EQUIFAX, UPDATE_RENT_PAYMENT_SETTLEMENT_STATUS } from '../../../../graphql/admin_mutations';
import AdminModal from '../../../../components/AdminModal';
import TextInput from '../../../../components/input';

import InfoIconNew from '../../../../svgs/InfoIconNew';

import styles from './RentPaymentModal.module.scss';

const RentPaymentModal = ({
  id,
  refetch,
  isShow,
  modalDetail,
  setModalDetail,
  setShowRentPaymentModal,
  setShowModalChildren,
  showModalChildren,
  payload,
}: IRentPaymentModal) => {
  const [reasonForRefund, setReasonForRefund] = useState('');
  const [isShowModalIcon, setIsShowModalIcon] = useState(true);
  const [updatePaymentStatus, setUpdatePaymentStatus] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const Toast = useToast();

  const [
    updateRentPaymentSettlementStatus,
    { loading: updateRentPaymentSettlementStatusLoading },
  ] = useMutation<
    { updateRentPaymentSettlementStatus: any },
    any
  >(UPDATE_RENT_PAYMENT_SETTLEMENT_STATUS);

  const [
    equifaxGenerateMetro2File,
    { loading: equifaxGenerateMetro2FileLoading },
  ] = useMutation<
    { equifaxGenerateMetro2File: any },
    any
  >(GENERATE_EQUIFAX_METRO_2_FILE);

  const [
    equifaxUpdateToSubmitted,
    { loading: equifaxUpdateToSubmittedLoading },
  ] = useMutation<
    { equifaxUpdateToSubmitted: any },
    any
  >(MARK_AS_SUBMITTED_TO_EQUIFAX);

  const handleConfirmClick = async (
  ) => {
    if (!updateRentPaymentSettlementStatusLoading) {
      try {
        const variables = {
          rentPaymentId: id,
          action: modalDetail?.action,
          ...(modalDetail?.action === 'refund' ? { reason: reasonForRefund } : { }),
        };
        const res = await updateRentPaymentSettlementStatus({ variables });
        if (
          res?.data?.updateRentPaymentSettlementStatus
          && res?.data?.updateRentPaymentSettlementStatus?.status === 200) {
          Toast.success(res?.data?.updateRentPaymentSettlementStatus?.message || 'Operation successful');
          refetch?.();
          setShowModalChildren(false);
          setIsSuccessful(true);
          setModalDetail({
            ...modalDetail,
            title: `${modalDetail?.action === 'settle' ? 'Settled' : 'Refunded'} Successfully!`,
            subTitle: `You have successfully ${modalDetail?.action === 'settle' ? 'settled' : 'refunded'} this transaction and it will be reflected on your dashboard.`,
          });
        } else {
          Toast.error(res?.data?.updateRentPaymentSettlementStatus?.message || 'Operation Failed');
        }
      } catch (error) {
        if (error && error?.graphQLErrors && error?.graphQLErrors.length > 0) {
          Toast.error(error?.graphQLErrors[0]?.message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  const downloadDoc = async (fileUrl: string) => {
    try {
      const result = await fetch(fileUrl);
      const blob = await result.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `METRO2FILE_${id}_${new Date().toISOString()}.txt`;
      link.click();
    } catch (error) {
      console.log('error', error);
      // window.open(fileUrl, '_blank');
    }
  };

  const generateEquifaxFile = async (
  ) => {
    if (!equifaxGenerateMetro2FileLoading) {
      try {
        const variables = {
          rentPaymentId: id,
        };
        const res = await equifaxGenerateMetro2File({ variables });
        if (
          res?.data?.equifaxGenerateMetro2File
          && res?.data?.equifaxGenerateMetro2File?.status === 200) {
          Toast.success(res?.data?.equifaxGenerateMetro2File?.message || 'Operation successful');
          refetch?.();
          setShowModalChildren(false);
          setIsSuccessful(true);
          await downloadDoc(res?.data?.equifaxGenerateMetro2File?.url);
          setModalDetail({
            ...modalDetail,
            title: 'Operation successful',
            subTitle: 'Equifax metro2 file has been downloaded successfully.',
            s3Url: res?.data?.equifaxGenerateMetro2File?.url,
          });
        } else {
          Toast.error(res?.data?.equifaxGenerateMetro2File?.message || 'Operation Failed');
        }
      } catch (error) {
        if (error && error?.graphQLErrors && error?.graphQLErrors.length > 0) {
          Toast.error(error?.graphQLErrors[0]?.message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  const markRentPaymentAsSubmittedToEquifax = async (
  ) => {
    if (!equifaxUpdateToSubmittedLoading) {
      try {
        const variables = {
          rentPaymentId: id,
        };
        const res = await equifaxUpdateToSubmitted({ variables });
        if (
          res?.data?.equifaxUpdateToSubmitted
          && res?.data?.equifaxUpdateToSubmitted?.status === 200) {
          Toast.success(res?.data?.equifaxUpdateToSubmitted?.message || 'Operation successful');
          refetch?.();
          setShowModalChildren(false);
          setIsSuccessful(true);
          setModalDetail({
            ...modalDetail,
            title: 'Operation successful',
            subTitle: 'Rent payment has been submitted to Equifax successfully.',
          });
        } else {
          Toast.error(res?.data?.equifaxUpdateToSubmitted?.message || 'Operation Failed');
        }
      } catch (error) {
        if (error && error?.graphQLErrors && error?.graphQLErrors.length > 0) {
          Toast.error(error?.graphQLErrors[0]?.message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  const handleModalAction = (type: string) => {
    if (!updatePaymentStatus && type === 'refund') {
      setModalDetail({
        ...modalDetail,
        subTitle: 'Kindly provide the reason for refunding this transaction.',
      });
      setShowRentPaymentModal(true);
      setShowModalChildren(true);
      setUpdatePaymentStatus(true);
      return;
    }
    if (type === 'settle') {
      setShowModalChildren(true);
      handleConfirmClick();
      return;
    }
    if (type === 'generateEquifaxFile') {
      setShowModalChildren(true);
      generateEquifaxFile();
    }
    if (type === 'markEquifaxFileAsSubmitted') {
      setShowModalChildren(true);
      markRentPaymentAsSubmittedToEquifax();
    }
  };

  const landlordsOrCompanyName = payload?.landlordType?.toLowerCase() === 'company'
    ? `${payload?.property?.landlord?.companyName}`
    : `${payload?.landLordName}` || `${payload?.property?.landlord?.firstName} ${payload?.property?.landlord?.lastName}`;
  const dynamicKey = payload?.landlordType?.toLowerCase() === 'company' ? 'Company' : 'Landlord';

  const landlordsDetails = [
    { id: 1, key: 'Landlord’s interac address', value: `${payload?.property?.landlord_e_transfer_email || payload?.landLordEmail || '********'}` },
    { id: 2, key: `${dynamicKey}’s name`, value: `${landlordsOrCompanyName || '********'}` },
    { id: 3, key: 'Rent amount', value: `${payload?.rentAmount || '********'}` },
  ];

  const getModalChildren = (type: string) => {
    if (type === 'generateEquifaxFile') {
      return (
        <div className={styles.children} />
      );
    }
    if (type === 'markEquifaxFileAsSubmitted') {
      return (
        <div className={styles.children} />
      );
    }
    if (!updatePaymentStatus && type === 'settle') {
      return (
        <div className={styles.children}>
          {landlordsDetails?.map(({ id, key, value }) => (
            <div key={id} className={styles.children__landlordInfo}>
              <h6 className={styles.children__landlordInfoKey}>{`${key}:`}</h6>
              <h6 className={styles.children__landlordInfoValue}>{value}</h6>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className={styles.children}>
        <TextInput
          name="reasonForRefund"
          type="text"
          value={reasonForRefund}
          backgroundColor="#F9F9F9"
          label="Reason for refund"
          width="100%"
          isRequired
          customOnChange={({ target: { value } }) => {
            setReasonForRefund(value);
          }}
          rows={5}
          multiline
        />
      </div>
    );
  };

  const handleCloseModal = () => {
    setIsShowModalIcon(true);
    setShowRentPaymentModal(false);
    setShowModalChildren(false);
    setUpdatePaymentStatus(false);
    setReasonForRefund('');
  };
  const loading = updateRentPaymentSettlementStatusLoading
  || equifaxGenerateMetro2FileLoading || equifaxUpdateToSubmittedLoading;
  return (
    <AdminModal
      headText={modalDetail?.title}
      subText={loading ? 'Processing...' : modalDetail?.subTitle}
      actionBtnName={modalDetail?.actionText || modalDetail?.action}
      handleModalAction={() => handleModalAction(modalDetail?.action)}
      topIcon={<InfoIconNew />}
      isShow={isShow}
      isActionBtnDisabled={updatePaymentStatus && !reasonForRefund}
      isSuccess={isSuccessful}
      isTopIcon={isShowModalIcon}
      onClickAway={handleCloseModal}
      onClose={handleCloseModal}
      isLoading={loading}
    >
      {showModalChildren && getModalChildren(modalDetail?.action)}
    </AdminModal>
  );
};

export default RentPaymentModal;
